import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * One-tap add for the bag's "Complete the set" card. Adds a single variant through
 * the same Ajax route and CartLinesUpdateEvent contract as the product form, so the
 * cart drawer re-renders in place (count, totals, free-shipping bar, the card itself).
 *
 * With data-remove="lineKey:newQty,..." it swaps those singles for the variant (the set
 * product) through /cart/update.js, so the set price applies.
 *
 * Markup: <v2-cart-add data-variant-id="123" data-error="..."><button type="button">…</button><span role="status"></span></v2-cart-add>
 */
class V2CartAdd extends HTMLElement {
  #abort = new AbortController();
  #timer = 0;

  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => this.add(), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    clearTimeout(this.#timer);
  }

  async add() {
    const button = this.querySelector('button');
    const id = this.dataset.variantId;
    if (!(button instanceof HTMLButtonElement) || button.disabled || !id) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', id);
    formData.append('quantity', '1');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: id, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      let response;
      if (this.dataset.remove) {
        // Swap to the set: take one of each owned single out and add the set variant in one request.
        /** @type {Record<string, number>} */
        const updates = {};
        for (const pair of this.dataset.remove.split(',').filter(Boolean)) {
          const [key, quantity] = pair.split(':');
          if (key) updates[key] = Number(quantity) || 0;
        }
        updates[id] = 1;
        response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', { body: JSON.stringify({ updates, sections: sectionIds.join(',') }) })
        ).then((res) => res.json());
      } else {
        const config = fetchConfig('javascript', { body: formData });
        response = await fetch(Theme.routes.cart_add_url, {
          ...config,
          headers: { ...config.headers, Accept: 'text/html' },
        }).then((res) => res.json());
      }
      const ajaxCart = await fetch(`${Theme.routes.cart_url}.json`).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'v2-cart-add', sourceId: id, itemCount: 1 },
        });
        this.#fail(button);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'v2-cart-add',
          sourceId: id,
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error instanceof Error ? error.message : 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#fail(button);
    }
  }

  /** @param {HTMLButtonElement} button */
  #fail(button) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    const status = this.querySelector('[role="status"]');
    if (!status || !this.dataset.error) return;
    status.textContent = this.dataset.error;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      status.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('v2-cart-add')) customElements.define('v2-cart-add', V2CartAdd);
